import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plug, RefreshCw, ShoppingCart, Upload, RotateCcw, Trash2, Package, Tag, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { errText } from "../../lib/errors";
import { fmtTRY, fmtDateTime, fmtQty4 } from "../../lib/format";

const inputCls = "w-full h-10 px-3 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";
const lbl = "text-xs font-semibold text-stone-600";
const btnCls = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors";
const btnPri = `${btnCls} bg-forest hover:bg-forest-dark text-white`;
const btnSec = `${btnCls} border border-stone-300 hover:border-forest text-stone-700`;

const TABS = [
  ["baglanti", "Bağlantı"], ["urunler", "Ürünler"], ["kategoriler", "Kategoriler"], ["stok", "Stok"],
  ["fiyat", "Fiyat"], ["siparisler", "Siparişler"], ["senkron", "Senkronizasyon"], ["loglar", "Loglar"], ["ayarlar", "Ayarlar"],
];

const MAP_UI = {
  synced: { label: "Eşleşti", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-800" },
  error: { label: "Hatalı", cls: "bg-red-100 text-red-700" },
  unmapped: { label: "Pasif", cls: "bg-stone-200 text-stone-600" },
};

const STATUS_UI = {
  active: { label: "Aktif", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  pending_auth: { label: "Yetkilendirme Bekleniyor", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-400" },
  not_configured: { label: "Yapılandırılmadı", cls: "bg-stone-200 text-stone-600", dot: "bg-stone-400" },
  error: { label: "Bağlantı Hatası", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

export default function AdminChannelDetail() {
  const { channelId } = useParams();
  const [ch, setCh] = useState(null);
  const [tab, setTab] = useState("baglanti");
  const [busy, setBusy] = useState("");
  const [syncResult, setSyncResult] = useState(null);
  const [ps, setPs] = useState(null);
  const [psQ, setPsQ] = useState("");
  const [psStatus, setPsStatus] = useState("");
  const [catMaps, setCatMaps] = useState(null);
  const [remoteCats, setRemoteCats] = useState(null);
  const [orders, setOrders] = useState(null);
  const [logs, setLogs] = useState(null);
  const [priceRule, setPriceRule] = useState({ type: "none", value: "" });
  const [settingsForm, setSettingsForm] = useState(null);
  const [mapForm, setMapForm] = useState(null);
  const [products, setProducts] = useState([]);

  const loadChannel = useCallback(() => {
    api.get("/admin/marketplaces").then(({ data }) => {
      const c = data.find((x) => x.channel_id === channelId);
      setCh(c || null);
      if (c) {
        setPriceRule(c.price_rule || { type: "none", value: "" });
        setSettingsForm({ name: c.name, logo: c.logo || "", is_active: c.is_active !== false, mode: c.mode || "stage", commission_rate: c.commission_rate ?? "", sync_prefs: { auto_stock: true, auto_price: true, auto_orders: true, ...(c.sync_prefs || {}) } });
      }
    }).catch(() => {});
  }, [channelId]);

  useEffect(() => { loadChannel(); }, [loadChannel]);

  const loadProductsStatus = useCallback(() => {
    const p = new URLSearchParams();
    if (psQ) p.set("q", psQ);
    if (psStatus) p.set("status", psStatus);
    api.get(`/admin/marketplaces/${channelId}/products-status?${p}`).then(({ data }) => setPs(data)).catch((e) => toast.error(errText(e)));
  }, [channelId, psQ, psStatus]);

  const loadCatMaps = useCallback(() => {
    api.get(`/admin/marketplaces/${channelId}/category-mappings`).then(({ data }) => setCatMaps(data)).catch((e) => toast.error(errText(e)));
  }, [channelId]);

  const loadOrders = useCallback(() => {
    api.get(`/admin/orders?kanal=${channelId}`).then(({ data }) => setOrders(data)).catch((e) => toast.error(errText(e)));
  }, [channelId]);

  const loadLogs = useCallback(() => {
    api.get(`/admin/marketplaces/${channelId}/sync-logs`).then(({ data }) => setLogs(data)).catch((e) => toast.error(errText(e)));
  }, [channelId]);

  useEffect(() => {
    if (tab === "urunler" || tab === "stok") loadProductsStatus();
    if (tab === "kategoriler") loadCatMaps();
    if (tab === "siparisler") loadOrders();
    if (tab === "loglar") loadLogs();
    if (tab === "fiyat") loadProductsStatus();
  }, [tab, loadProductsStatus, loadCatMaps, loadOrders, loadLogs]);

  const run = async (path, label) => {
    setBusy(path);
    setSyncResult(null);
    try {
      const { data } = await api.post(`/admin/marketplaces/${channelId}/${path}`, {});
      setSyncResult({ label, data });
      toast.success(`${label} tamamlandı`);
      loadChannel();
      if (tab === "urunler" || tab === "stok" || tab === "fiyat") loadProductsStatus();
    } catch (e) { toast.error(errText(e) || "İşlem başarısız"); }
    finally { setBusy(""); }
  };

  const testConn = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/marketplaces/${channelId}/test`);
      if (data.ok) toast.success(data.message); else toast.warning(data.message);
      loadChannel();
    } catch { toast.error("Bağlantı testi başarısız"); }
    finally { setBusy(""); }
  };

  const savePriceRule = async () => {
    setBusy("pricerule");
    try {
      await api.put(`/admin/marketplaces/${channelId}`, { price_rule: { type: priceRule.type, value: parseFloat(priceRule.value) || 0 } });
      toast.success("Fiyat kuralı kaydedildi — sonraki senkronizasyonda uygulanır");
      loadChannel();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const saveSettings = async () => {
    setBusy("settings");
    try {
      const payload = { name: settingsForm.name, logo: settingsForm.logo, is_active: settingsForm.is_active, mode: settingsForm.mode, sync_prefs: settingsForm.sync_prefs };
      if (settingsForm.commission_rate !== "" && settingsForm.commission_rate !== null) payload.commission_rate = parseFloat(settingsForm.commission_rate);
      await api.put(`/admin/marketplaces/${channelId}`, payload);
      toast.success("Kanal ayarları kaydedildi");
      loadChannel();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const saveCatMap = async (m) => {
    try {
      await api.put(`/admin/marketplaces/${channelId}/category-mappings`, { mappings: [m] });
      toast.success("Kategori eşleştirmesi kaydedildi");
    } catch (e) { toast.error(errText(e)); }
  };

  const fetchRemoteCats = async () => {
    setBusy("cats");
    try {
      const { data } = await api.get(`/admin/marketplaces/${channelId}/remote-categories`);
      setRemoteCats(data.categories || []);
      toast.success(`${data.count} kanal kategorisi çekildi`);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const startMap = () => {
    if (products.length) { setMapForm({ product_id: "", remote_barcode: "", remote_sku: "", remote_category_id: "", price_override: "" }); return; }
    api.get("/admin/products").then(({ data }) => {
      setProducts((Array.isArray(data) ? data : data.items || []).filter((p) => p.is_active !== false));
      setMapForm({ product_id: "", remote_barcode: "", remote_sku: "", remote_category_id: "", price_override: "" });
    }).catch((e) => toast.error(errText(e)));
  };

  const saveMap = async () => {
    if (!mapForm?.product_id) { toast.warning("Ürün seçin"); return; }
    setBusy("map");
    try {
      await api.post(`/admin/marketplaces/${channelId}/listings`, {
        product_id: mapForm.product_id,
        remote_barcode: mapForm.remote_barcode || "", remote_sku: mapForm.remote_sku || "",
        remote_category_id: mapForm.remote_category_id || "",
        price_override: mapForm.price_override ? parseFloat(mapForm.price_override) : null,
      });
      toast.success("Ürün eşleştirildi");
      setMapForm(null);
      loadProductsStatus();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(""); }
  };

  const delMap = async (lst) => {
    if (!window.confirm("Bu eşleştirme silinsin mi?")) return;
    try {
      await api.delete(`/admin/marketplace-listings/${lst.listing_id}`);
      toast.success("Eşleştirme silindi");
      loadProductsStatus();
    } catch { toast.error("Silinemedi"); }
  };

  if (!ch) return <div className="p-10 text-center text-stone-400 text-sm">Kanal yükleniyor...</div>;
  const st = STATUS_UI[ch.status] || STATUS_UI.not_configured;

  return (
    <div data-testid="channel-detail-page" className="space-y-5 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/admin/pazaryerleri" data-testid="channel-back" className={`${btnSec} !px-2.5`}><ArrowLeft className="w-4 h-4" /></Link>
        {ch.logo ? <img src={ch.logo} alt="" className="w-9 h-9 rounded-lg object-contain ring-1 ring-stone-200" /> : <div className="w-9 h-9 rounded-lg bg-cream-dark flex items-center justify-center"><Tag className="w-4 h-4 text-forest" /></div>}
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-xl sm:text-2xl text-stone-900 flex items-center gap-2">{ch.name}
            <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ch.mode === "live" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>{ch.mode === "live" ? "CANLI" : "TEST"}</span>
          </h1>
        </div>
      </div>

      {ch.last_error && <div data-testid="channel-last-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl px-4 py-3"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{ch.last_error}</div>}

      <div data-testid="channel-tabs" className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`channel-tab-${id}`} onClick={() => setTab(id)} className={`px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${tab === id ? "bg-forest text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-forest"}`}>{label}</button>
        ))}
      </div>

      {tab === "baglanti" && (
        <div data-testid="tab-panel-baglanti" className="bg-white rounded-2xl border border-stone-200/80 p-6 space-y-4">
          <h2 className="font-semibold text-stone-900">Bağlantı Durumu</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-cream/60 rounded-xl p-3"><p className="text-[11px] text-stone-500">Durum</p><p className="font-semibold">{st.label}</p></div>
            <div className="bg-cream/60 rounded-xl p-3"><p className="text-[11px] text-stone-500">Mod</p><p className="font-semibold">{ch.mode === "live" ? "Canlı" : "Test (Stage)"}</p></div>
            <div className="bg-cream/60 rounded-xl p-3"><p className="text-[11px] text-stone-500">Son Senkron</p><p className="font-semibold">{ch.last_sync ? fmtDateTime(ch.last_sync) : "—"}</p></div>
            <div className="bg-cream/60 rounded-xl p-3"><p className="text-[11px] text-stone-500">Eşleşen Ürün</p><p className="font-semibold">{ch.product_count || 0}</p></div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="channel-test-btn" onClick={testConn} disabled={busy === "test"} className={btnPri}>{busy === "test" ? "Test ediliyor..." : "Bağlantıyı Test Et"}</button>
            <button data-testid="channel-goto-settings" onClick={() => setTab("ayarlar")} className={btnSec}><Plug className="w-3.5 h-3.5" /> API Bilgileri / Ayarlar</button>
          </div>
          <p className="text-[11px] text-stone-400">API anahtarları Ana paneldeki "Bağlan" penceresinden girilir. Kimlik bilgileri yalnızca sunucuda saklanır ve maskelenir.</p>
        </div>
      )}

      {(tab === "urunler" || tab === "stok" || tab === "fiyat") && ps && (
        <div data-testid={`tab-panel-${tab}`} className="space-y-4">
          {tab === "fiyat" && (
            <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
              <h2 className="font-semibold text-stone-900">Kanal Fiyat Kuralı</h2>
              <p className="text-xs text-stone-500 mt-1">Ana fiyat üzerine otomatik uygulanır. Ürün bazlı "özel fiyat" her zaman kuralı ezer.</p>
              <div className="flex items-end gap-3 mt-3 flex-wrap">
                <div><label className={lbl}>Kural Tipi</label>
                  <select data-testid="price-rule-type" className={inputCls} value={priceRule.type} onChange={(e) => setPriceRule({ ...priceRule, type: e.target.value })}>
                    <option value="none">Kural Yok (ana fiyat)</option>
                    <option value="percent">Yüzde Ekle (%)</option>
                    <option value="fixed">Sabit Tutar Ekle (₺)</option>
                  </select></div>
                <div><label className={lbl}>Değer</label><input data-testid="price-rule-value" type="number" step="0.01" className={inputCls} value={priceRule.value} onChange={(e) => setPriceRule({ ...priceRule, value: e.target.value })} placeholder="örn. 10" /></div>
                <button data-testid="price-rule-save" onClick={savePriceRule} disabled={busy === "pricerule"} className={btnPri}>Kuralı Kaydet</button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <h2 className="font-semibold text-stone-900 flex-1">Ürün Eşleştirme{tab === "stok" ? " — Stok Durumu" : tab === "fiyat" ? " — Fiyatlar" : ""}</h2>
              {ps.summary && (
                <div className="flex gap-1.5">
                  {Object.entries(MAP_UI).map(([k, ui]) => (
                    <button key={k} data-testid={`map-filter-${k}`} onClick={() => setPsStatus(psStatus === k ? "" : k)} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${ui.cls} ${psStatus === k ? "ring-2 ring-forest" : "opacity-70"}`}>{ui.label}: {ps.summary[k] ?? 0}</button>
                  ))}
                </div>
              )}
              <input data-testid="map-search" className={`${inputCls} !w-52`} placeholder="Ürün ara..." value={psQ} onChange={(e) => setPsQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadProductsStatus()} />
              <button data-testid="map-search-btn" onClick={loadProductsStatus} className={btnSec}>Ara</button>
              <button data-testid="map-add-btn" onClick={startMap} className={btnPri}><Package className="w-3.5 h-3.5" /> Ürün Eşleştir</button>
            </div>
            {mapForm && (
              <div data-testid="map-form" className="bg-cream/50 border border-stone-200 rounded-xl p-4 mb-3 grid sm:grid-cols-5 gap-3">
                <select data-testid="map-product-select" className={inputCls} value={mapForm.product_id} onChange={(e) => { const p = products.find((x) => x.product_id === e.target.value); setMapForm({ ...mapForm, product_id: e.target.value, remote_barcode: p?.barcode || "", remote_sku: p?.sku || "" }); }}>
                  <option value="">Ürün seçin...</option>
                  {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                </select>
                <input data-testid="map-remote-barcode" className={inputCls} placeholder="Kanal barkodu" value={mapForm.remote_barcode} onChange={(e) => setMapForm({ ...mapForm, remote_barcode: e.target.value })} />
                <input data-testid="map-remote-sku" className={inputCls} placeholder="Kanal SKU" value={mapForm.remote_sku} onChange={(e) => setMapForm({ ...mapForm, remote_sku: e.target.value })} />
                <input data-testid="map-price-override" type="number" step="0.01" className={inputCls} placeholder="Özel fiyat (ops.)" value={mapForm.price_override} onChange={(e) => setMapForm({ ...mapForm, price_override: e.target.value })} />
                <div className="flex gap-2"><button data-testid="map-save" onClick={saveMap} disabled={busy === "map"} className={btnPri}>Kaydet</button><button data-testid="map-cancel" onClick={() => setMapForm(null)} className={btnSec}>Vazgeç</button></div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase">
                  <tr><th className="text-left px-3 py-2">ATA Ürünü</th><th className="text-left px-3 py-2">Durum</th>
                    {tab === "stok" ? <><th className="text-right px-3 py-2">ATA Stok</th><th className="text-right px-3 py-2">Son Gönderilen</th></>
                    : <><th className="text-right px-3 py-2">Ana Fiyat</th><th className="text-right px-3 py-2">Kanal Fiyatı</th></>}
                    <th className="text-left px-3 py-2">Kanal SKU/Barkod</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody>
                  {ps.items.map((it) => (
                    <tr key={it.product.product_id} data-testid={`map-row-${it.product.product_id}`} className="border-t border-stone-100">
                      <td className="px-3 py-2"><p className="font-medium text-stone-800">{it.product.name}</p><p className="text-[11px] text-stone-400 font-mono">{it.product.sku || it.product.barcode || ""}</p></td>
                      <td className="px-3 py-2"><span data-testid={`map-status-${it.product.product_id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${MAP_UI[it.map_status].cls}`}>{MAP_UI[it.map_status].label}</span>
                        {it.listing?.last_error && <p className="text-[10px] text-red-500 mt-0.5 max-w-[200px] truncate" title={it.listing.last_error}>{it.listing.last_error}</p>}</td>
                      {tab === "stok"
                        ? <><td className="px-3 py-2 text-right font-mono">{fmtQty4(it.product.stock, it.product.base_unit || it.product.unit || "")}</td><td className="px-3 py-2 text-right font-mono text-stone-500">{it.listing?.last_pushed_stock != null ? fmtQty4(it.listing.last_pushed_stock, it.product.base_unit || it.product.unit || "") : "—"}</td></>
                        : <><td className="px-3 py-2 text-right">{fmtTRY(it.product.discount_price || it.product.price)}</td><td className="px-3 py-2 text-right font-semibold text-forest">{it.listing ? fmtTRY(it.channel_price) : "—"}</td></>}
                      <td className="px-3 py-2 text-xs font-mono text-stone-500">{it.listing ? (it.listing.remote_sku || it.listing.remote_barcode || "—") : "—"}</td>
                      <td className="px-3 py-2 text-right">{it.listing && <button data-testid={`map-del-${it.listing.listing_id}`} onClick={() => delMap(it.listing)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                    </tr>
                  ))}
                  {!ps.items.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-stone-400 text-sm">Kayıt yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {(tab === "urunler" || tab === "stok" || tab === "fiyat") && !ps && <div className="p-10 text-center text-stone-400 text-sm">Yükleniyor...</div>}

      {tab === "kategoriler" && (
        <div data-testid="tab-panel-kategoriler" className="bg-white rounded-2xl border border-stone-200/80 p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-semibold text-stone-900 flex-1">Kategori Eşleştirme</h2>
            <button data-testid="fetch-remote-cats" onClick={fetchRemoteCats} disabled={busy === "cats"} className={btnSec}><RefreshCw className="w-3.5 h-3.5" /> {busy === "cats" ? "Çekiliyor..." : "Kanal Kategorilerini Çek"}</button>
          </div>
          <p className="text-xs text-stone-500">Soldaki ATA Angora kategorisini kanalın kategori ID'si ile eşleştirin. Otomatik eşleşme yoksa manuel seçim yapın.</p>
          {!catMaps && <p className="text-sm text-stone-400">Yükleniyor...</p>}
          {catMaps && (
            <div className="space-y-2">
              {catMaps.map((c) => (
                <div key={c.category_id} data-testid={`catmap-row-${c.category_id}`} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-center border border-stone-100 rounded-xl p-3">
                  <p className="text-sm font-medium text-stone-800">{c.name}</p>
                  {remoteCats?.length ? (
                    <select data-testid={`catmap-select-${c.category_id}`} className={inputCls} value={c.remote_category_id}
                      onChange={(e) => { const rc = remoteCats.find((r) => String(r.id ?? r.categoryId ?? r.code) === e.target.value); saveCatMap({ category_id: c.category_id, remote_category_id: e.target.value, remote_category_name: rc ? String(rc.name ?? rc.title ?? "") : "" }); loadCatMaps(); }}>
                      <option value="">— Eşleştirilmedi —</option>
                      {remoteCats.map((r) => { const rid = String(r.id ?? r.categoryId ?? r.code ?? ""); return <option key={rid} value={rid}>{r.name ?? r.title ?? rid}</option>; })}
                    </select>
                  ) : (
                    <input data-testid={`catmap-input-${c.category_id}`} className={inputCls} placeholder="Kanal kategori ID" defaultValue={c.remote_category_id}
                      onKeyDown={(e) => { if (e.key === "Enter") { saveCatMap({ category_id: c.category_id, remote_category_id: e.target.value, remote_category_name: "" }); } }}
                      onBlur={(e) => { if (e.target.value !== c.remote_category_id) { saveCatMap({ category_id: c.category_id, remote_category_id: e.target.value, remote_category_name: "" }); } }} />
                  )}
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full text-center ${c.remote_category_id ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-500"}`}>{c.remote_category_id ? "Eşleşti" : "Bekliyor"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "siparisler" && (
        <div data-testid="tab-panel-siparisler" className="bg-white rounded-2xl border border-stone-200/80 p-5">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-semibold text-stone-900 flex-1">Kanal Siparişleri</h2>
            <button data-testid="channel-pull-orders" onClick={() => run("pull-orders", "Sipariş çekme")} disabled={!!busy} className={btnSec}><ShoppingCart className="w-3.5 h-3.5" /> Siparişleri Çek</button>
          </div>
          {!orders && <p className="text-sm text-stone-400">Yükleniyor...</p>}
          {orders && (
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[520px]">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase"><tr><th className="text-left px-3 py-2">Sipariş No</th><th className="text-left px-3 py-2">Müşteri</th><th className="text-right px-3 py-2">Tutar</th><th className="text-left px-3 py-2">Durum</th><th className="text-left px-3 py-2">Tarih</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} data-testid={`channel-order-${o.order_number}`} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-mono text-xs font-bold">{o.order_number}</td>
                    <td className="px-3 py-2">{o.address?.full_name || o.email}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtTRY(o.total)}</td>
                    <td className="px-3 py-2"><span className="text-[11px] font-semibold bg-cream px-2 py-0.5 rounded-full">{o.status}</span></td>
                    <td className="px-3 py-2 text-xs text-stone-500">{fmtDateTime(o.created_at)}</td>
                  </tr>
                ))}
                {!orders.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-stone-400 text-sm">Bu kanaldan sipariş yok</td></tr>}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {tab === "senkron" && (
        <div data-testid="tab-panel-senkron" className="space-y-4">
          <div className="bg-white rounded-2xl border border-stone-200/80 p-5">
            <h2 className="font-semibold text-stone-900 mb-3">Senkronizasyon Merkezi</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <button data-testid="sync-products-btn" onClick={() => run("push-listings", "Ürün gönderimi")} disabled={!!busy} className={`${btnPri} justify-center py-3`}><Upload className="w-4 h-4" /> {busy === "push-listings" ? "Gönderiliyor..." : "Ürünleri Gönder"}</button>
              <button data-testid="sync-stock-price-btn" onClick={() => run("sync-stock-price", "Stok/fiyat eşitleme")} disabled={!!busy} className={`${btnCls} justify-center py-3 bg-gold hover:bg-gold-dark text-stone-900`}><RefreshCw className="w-4 h-4" /> {busy === "sync-stock-price" ? "Eşitleniyor..." : "Stok/Fiyat Eşitle"}</button>
              <button data-testid="sync-orders-btn" onClick={() => run("pull-orders", "Sipariş çekme")} disabled={!!busy} className={`${btnSec} justify-center py-3`}><ShoppingCart className="w-4 h-4" /> {busy === "pull-orders" ? "Çekiliyor..." : "Siparişleri Çek"}</button>
              <button data-testid="sync-claims-btn" onClick={() => run("pull-claims", "İade çekme")} disabled={!!busy} className={`${btnSec} justify-center py-3`}><RotateCcw className="w-4 h-4" /> {busy === "pull-claims" ? "Çekiliyor..." : "İadeleri Çek"}</button>
            </div>
            {syncResult && (
              <div data-testid="sync-result" className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
                <p className="font-semibold text-emerald-900">{syncResult.label} sonucu:</p>
                <p className="text-emerald-800 mt-1 font-mono text-xs">
                  {syncResult.data.pushed !== undefined && `Başarılı: ${syncResult.data.pushed} · Hatalı: ${syncResult.data.failed || 0}`}
                  {syncResult.data.items !== undefined && `Gönderilen ürün: ${syncResult.data.items}`}
                  {syncResult.data.fetched !== undefined && `Çekilen: ${syncResult.data.fetched} · Yeni: ${syncResult.data.imported ?? syncResult.data.new ?? 0} · Atlandı: ${syncResult.data.skipped || 0}`}
                </p>
                {(syncResult.data.errors || []).length > 0 && <p className="text-red-600 text-xs mt-1">{syncResult.data.errors[0]}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "loglar" && (
        <div data-testid="tab-panel-loglar" className="bg-white rounded-2xl border border-stone-200/80 p-5">
          <h2 className="font-semibold text-stone-900 mb-3">Entegrasyon Kayıtları</h2>
          {!logs && <p className="text-sm text-stone-400">Yükleniyor...</p>}
          {logs && (
            <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
              {logs.map((l) => (
                <div key={l.log_id} data-testid={`log-row-${l.log_id}`} className={`text-xs rounded-lg px-3 py-2 border ${l.ok ? "border-stone-100 bg-stone-50" : "border-red-200 bg-red-50"}`}>
                  <span className={`font-bold ${l.ok ? "text-emerald-700" : "text-red-600"}`}>{l.ok ? "OK" : "HATA"}</span>
                  <span className="font-mono text-stone-500 mx-2">{l.action}</span>
                  <span className="text-stone-700">{l.message}</span>
                  <span className="block text-[10px] text-stone-400 mt-0.5">{fmtDateTime(l.created_at)}</span>
                </div>
              ))}
              {!logs.length && <p className="text-sm text-stone-400 text-center py-6">Kayıt yok</p>}
            </div>
          )}
        </div>
      )}

      {tab === "ayarlar" && settingsForm && (
        <div data-testid="tab-panel-ayarlar" className="bg-white rounded-2xl border border-stone-200/80 p-6 space-y-4">
          <h2 className="font-semibold text-stone-900">Kanal Ayarları</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Kanal Adı</label><input data-testid="chset-name" className={inputCls} value={settingsForm.name} onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })} /></div>
            <div><label className={lbl}>Logo URL</label><input data-testid="chset-logo" className={inputCls} value={settingsForm.logo} onChange={(e) => setSettingsForm({ ...settingsForm, logo: e.target.value })} /></div>
            <div><label className={lbl}>Mod</label>
              <select data-testid="chset-mode" className={inputCls} value={settingsForm.mode} onChange={(e) => setSettingsForm({ ...settingsForm, mode: e.target.value })}>
                <option value="stage">Test (Stage)</option><option value="live">Canlı (Live)</option>
              </select></div>
            <div><label className={lbl}>Komisyon Oranı (%)</label><input data-testid="chset-commission" type="number" step="0.01" className={inputCls} value={settingsForm.commission_rate} onChange={(e) => setSettingsForm({ ...settingsForm, commission_rate: e.target.value })} /></div>
          </div>
          <div className="flex gap-4 flex-wrap">
            {[["auto_stock", "Otomatik stok"], ["auto_price", "Otomatik fiyat"], ["auto_orders", "Otomatik sipariş çekme"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-stone-700">
                <input data-testid={`chset-sync-${k}`} type="checkbox" className="w-4 h-4 accent-forest" checked={!!settingsForm.sync_prefs[k]} onChange={(e) => setSettingsForm({ ...settingsForm, sync_prefs: { ...settingsForm.sync_prefs, [k]: e.target.checked } })} /> {label}
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input data-testid="chset-active" type="checkbox" className="w-4 h-4 accent-forest" checked={settingsForm.is_active} onChange={(e) => setSettingsForm({ ...settingsForm, is_active: e.target.checked })} /> Kanal aktif
            </label>
          </div>
          <button data-testid="chset-save" onClick={saveSettings} disabled={busy === "settings"} className={btnPri}>{busy === "settings" ? "Kaydediliyor..." : "Ayarları Kaydet"}</button>
        </div>
      )}
    </div>
  );
}
